import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { VatMode, grossPriceFromNet, netPriceFromGross } from '@crm/shared'
import {
  getQuotations,
  getQuotationDetail,
  createQuotation,
  deleteQuotation,
  type QuotationSnapshot,
  type QuotationItem,
} from '../../../shared/api/quotations.api'
import { getRequest } from '../../../shared/api/requests.api'
import { getExchangeRates, getMarkupConstants } from '../../../shared/api/exchangeRates.api'
import { downloadKp, type KpDownloadPayload, type KpFormat } from '../utils/downloadKp'
import { kpRequestNumber } from '../utils/kpRequestNumber'
import { kpVatView } from '../utils/kpVat'
import { confirm } from '../../../shared/ui/confirmStore'
import { toast } from '../../../shared/ui/toastStore'

/** Русская причина от сервера (response.data.error.message) или запасной текст. */
const apiErrorText = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? fallback
import { useAuthStore } from '../../auth/authStore'
import { KPDetailsModal } from './KPDetailsModal'
import { KpDownloadModal } from './KpDownloadModal'

// Данные для скачивания КП без выбора формата/юрлица — их добавит модалка.
type PendingKp = Omit<KpDownloadPayload, 'entityId'>

function snapshotItems(snapshot: QuotationSnapshot, custPnByItemId?: Map<string, string | null>) {
  return (snapshot.items as QuotationItem[]).map((it) => ({
    partNumber: it.partNumber,
    // Снапшот может быть старым (до появления поля) — тогда берём оригинальный
    // артикул заказчика из текущих позиций заявки по itemId, иначе фолбэк на partNumber.
    customerPartNumber: it.customerPartNumber ?? custPnByItemId?.get(it.itemId) ?? it.partNumber,
    brand: it.brand,
    quantity: it.quantity,
    unit: it.unit,
    priceWithMarkup: it.priceWithMarkup,
    priceNet: it.priceNet,
    matrix: it.matrix,
    note: it.note ?? null,
  }))
}

interface KPPanelProps {
  requestId: string
}

export function KPPanel({ requestId }: KPPanelProps) {
  const queryClient = useQueryClient()
  const [detailId, setDetailId] = useState<string | null>(null)
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingKp | null>(null)
  const currentUser = useAuthStore((s) => s.user)

  const { data: quotations = [] } = useQuery({
    queryKey: ['quotations', requestId],
    queryFn: () => getQuotations(requestId),
  })

  // request нужен модалу деталей — из него берём items для колонки «Статус».
  // Запрос уже закэширован RequestDetailPage, здесь повторно не летит.
  const { data: request } = useQuery({
    queryKey: ['requests', requestId],
    queryFn: () => getRequest(requestId),
  })

  const { data: detail } = useQuery({
    queryKey: ['quotation-detail', detailId],
    queryFn: () => getQuotationDetail(detailId!),
    enabled: !!detailId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteQuotation(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['quotations', requestId] })
      // Бэк мог снять «Счёт»/«Отказ» с позиций этого КП — обновляем и заявку.
      void queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
      setDeletingId(null)
      // Если удалили тот, что открыт — закрыть модал.
      if (detailId && deletingId === detailId) setDetailId(null)
    },
    onError: () => setDeletingId(null),
  })

  // Повторное скачивание сохранённого КП. Открываем модалку выбора формата/юрлица.
  // Ответственный — автор снапшота (кто изначально выставил это КП), а не тот,
  // кто сейчас перекачивает файл.
  const handleDownload = (snapshot: QuotationSnapshot) => {
    if (!snapshot.items) return
    const custPnByItemId = new Map<string, string | null>(
      (request?.items ?? []).map((i) => [i.id, i.customerPartNumber]),
    )
    setPending({
      requestNumber: kpRequestNumber(snapshot.requestNumber, request?.requestNumber),
      clientName: request?.client?.name ?? null,
      responsibleName: snapshot.createdBy?.name ?? snapshot.createdBy?.email ?? null,
      vatMode: snapshot.vatMode,
      items: snapshotItems(snapshot, custPnByItemId),
    })
  }

  // Обновить КП с актуальными курсами/константами.
  //
  // Логика по просьбе Сергея (21.04):
  //   - Если и курсы, и константы совпадают со старым снапшотом → НЕ создаём
  //     новый, пишем toast «КП не изменилось, скачан старый» и даём тот же
  //     Excel что был. Это сохраняет единую историю КП для заказчика.
  //   - Если что-то изменилось (только курсы / только константы / оба)
  //     пересчитываем cost пропорционально изменениям и создаём НОВЫЙ
  //     снапшот (история остаётся — старые КП никуда не деваются).
  //
  // Формула восстановления новой себестоимости для позиции:
  //   cost_new = cost_old × (constant_new / constant_old) × (rate_new / rate_old)
  //   Для RF курсовой множитель = 1 (цены изначально в ₽).
  //   Для остальных матриц — курс CNY (см. computeBestPrice.ts::toRub).
  const handleUpdateKP = async (snapshot: QuotationSnapshot) => {
    if (!snapshot.items) {
      const full = await getQuotationDetail(snapshot.id)
      if (!full.items) return
      snapshot = full
    }
    setUpdatingId(snapshot.id)
    try {
      const newRates = await getExchangeRates()
      const newConstants = (await getMarkupConstants()) as Record<string, number>
      const oldRates = snapshot.exchangeRates as Record<string, number>
      const oldConstants = snapshot.markupConstants as Record<string, number>
      const markups = snapshot.markups as Record<string, number>
      const overrides = (snapshot.positionOverrides ?? {}) as Record<string, number>
      const vatMode = snapshot.vatMode ?? VatMode.INCLUDED

      const ratesChanged =
        (oldRates.USD ?? 0) !== newRates.USD ||
        (oldRates.EUR ?? 0) !== newRates.EUR ||
        (oldRates.CNY ?? 0) !== newRates.CNY
      const constantsChanged = ['STOCK', 'CHINA', 'RF', 'MANUFACTURER', 'OTHER'].some(
        (k) => (oldConstants[k] ?? 0) !== (newConstants[k] ?? 0),
      )

      if (!ratesChanged && !constantsChanged) {
        toast.info(
          'Курсы и наценочные константы не изменились — КП осталось прежним, скачиваю старое.',
        )
        handleDownload(snapshot)
        return
      }

      const oldItems = snapshot.items as QuotationItem[]
      const newItems: QuotationItem[] = oldItems.map((it) => {
        const matrixKey =
          it.matrix === 'stock'
            ? 'STOCK'
            : it.matrix === 'china'
              ? 'CHINA'
              : it.matrix === 'rf'
                ? 'RF'
                : it.matrix === 'manuf'
                  ? 'MANUFACTURER'
                  : 'OTHER'
        const oldConst = oldConstants[matrixKey] ?? 1
        const newConst = newConstants[matrixKey] ?? oldConst
        const constRatio = oldConst === 0 ? 1 : newConst / oldConst

        const isRF = matrixKey === 'RF'
        const oldCny = oldRates.CNY ?? 0
        const newCny = newRates.CNY ?? 0
        const rateRatio = isRF || oldCny === 0 ? 1 : newCny / oldCny

        const newCost = it.cost * constRatio * rateRatio
        const markup = overrides[it.itemId] ?? markups[it.matrix] ?? 1
        const priceWithMarkup = Math.round(newCost * markup * 100) / 100
        const base = { ...it, cost: Math.round(newCost * 100) / 100, markup }
        // КП «НДС сверху» обновляется в том же режиме: цена без НДС — из новой цены с наценкой.
        if (vatMode === VatMode.ON_TOP) {
          const priceNet = netPriceFromGross(priceWithMarkup)
          return { ...base, priceNet, priceWithMarkup: grossPriceFromNet(priceNet) }
        }
        return { ...base, priceWithMarkup, priceNet: undefined }
      })

      const totalSum = Math.round(kpVatView(newItems, vatMode).total * 100) / 100
      // Пустой номер старого снимка не копируем — берём текущий номер заявки.
      const requestNumber = kpRequestNumber(snapshot.requestNumber, request?.requestNumber)

      await createQuotation(requestId, {
        requestNumber: requestNumber ?? null,
        exchangeRates: { USD: newRates.USD, EUR: newRates.EUR, CNY: newRates.CNY },
        markupConstants: { ...newConstants },
        markups,
        positionOverrides: overrides,
        items: newItems,
        totalSum,
        vatMode,
      })

      void queryClient.invalidateQueries({ queryKey: ['quotations', requestId] })

      // Пересчитанное КП выставляет текущий пользователь — он и ответственный.
      setPending({
        requestNumber,
        clientName: request?.client?.name ?? null,
        responsibleName: currentUser?.name ?? currentUser?.email ?? null,
        vatMode,
        items: newItems.map((it) => ({
          partNumber: it.partNumber,
          brand: it.brand,
          quantity: it.quantity,
          unit: it.unit,
          priceWithMarkup: it.priceWithMarkup,
          priceNet: it.priceNet,
          matrix: it.matrix,
        })),
      })
    } catch (err) {
      // Раньше обработчика не было: отказ сервера (422 с номером строки) уходил
      // в необработанный reject, и кнопка молча возвращалась в «Обновить».
      toast.error(`КП не обновлено: ${apiErrorText(err, 'Не удалось пересчитать КП')}`)
    } finally {
      setUpdatingId(null)
    }
  }

  const handleDelete = async (snapshot: QuotationSnapshot) => {
    const date = new Date(snapshot.createdAt).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    // Позиции, чей текущий статус «Счёт»/«Отказ клиента» оформлен именно из
    // этого КП, — бэк снимет его при удалении (иначе они «застревали» в заказе
    // без пути отката). Позицию, отказанную здесь, но оформленную в «Счёт» из
    // другого КП, бэк не тронет — в счётчик её не включаем.
    const affected = (request?.items ?? []).filter(
      (i) =>
        (i.status === 'ORDERED' && i.orderedFromQuotationId === snapshot.id) ||
        (i.status === 'REJECTED_BY_CUSTOMER' && i.rejectedFromQuotationId === snapshot.id),
    ).length
    if (
      !(await confirm({
        title: `Удалить КП от ${date}?`,
        message:
          affected > 0
            ? `Из этого КП оформлено позиций: ${affected} («Счёт»/«Отказ клиента») — их статусы будут сняты, позиции вернутся в «КП создано». Позиции из других КП не изменятся.`
            : 'Статусы позиций из других КП не изменятся.',
        danger: true,
        confirmText: 'Удалить',
      }))
    )
      return
    setDeletingId(snapshot.id)
    deleteMutation.mutate(snapshot.id)
  }

  if (quotations.length === 0) {
    return <p className="text-xs text-gray-400">Нет сохранённых КП</p>
  }

  return (
    <div className="space-y-2">
      {quotations.map((q) => {
        const totalItems = (q.items as QuotationItem[] | undefined)?.length ?? 0
        const ordered = q.orderedCount ?? 0
        return (
          <div
            key={q.id}
            className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2"
          >
            <div className="min-w-0">
              <div className="text-xs font-medium text-gray-700 flex items-center gap-2 flex-wrap">
                КП от{' '}
                {new Date(q.createdAt).toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {ordered > 0 && (
                  <span className="inline-block px-2 py-0.5 text-[10px] rounded bg-emerald-100 text-emerald-700">
                    Счёт {ordered}/{totalItems}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-400">
                {q.createdBy ? q.createdBy.name || q.createdBy.email : 'удалённый пользователь'} ·{' '}
                {q.totalSum.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setDetailId(q.id)}
                className="px-2 py-1 text-xs text-brand-600 hover:bg-brand-50 rounded transition-colors"
                title="Детали КП"
              >
                Детали
              </button>
              <button
                onClick={() =>
                  void getQuotationDetail(q.id)
                    .then(handleUpdateKP)
                    .catch((e: unknown) => toast.error(apiErrorText(e, 'Не удалось загрузить КП')))
                }
                disabled={updatingId === q.id}
                className="px-2 py-1 text-xs text-amber-600 hover:bg-amber-50 rounded transition-colors disabled:opacity-50"
                title="Пересчитать с актуальными курсами"
              >
                {updatingId === q.id ? '...' : 'Обновить'}
              </button>
              <button
                onClick={() => {
                  void getQuotationDetail(q.id)
                    .then(handleDownload)
                    .catch((e: unknown) => toast.error(apiErrorText(e, 'Не удалось скачать КП')))
                }}
                className="px-2 py-1 text-xs text-green-600 hover:bg-green-50 rounded transition-colors"
                title="Скачать КП"
              >
                Скачать
              </button>
              <button
                onClick={() => handleDelete(q)}
                disabled={deletingId === q.id}
                className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title="Удалить КП"
              >
                {deletingId === q.id ? '...' : 'Удалить'}
              </button>
            </div>
          </div>
        )
      })}

      {detailId && detail && (
        <KPDetailsModal
          requestId={requestId}
          detail={detail}
          requestItems={request?.items ?? []}
          onClose={() => setDetailId(null)}
          onDownload={handleDownload}
        />
      )}

      {pending && (
        <KpDownloadModal
          defaultEntityId={request?.sellerEntityId}
          onClose={() => setPending(null)}
          onPick={(format: KpFormat, entityId: string, recipient: string) =>
            // Повторное скачивание сохранённого КП: телефон не подставляем (автор
            // снапшота мог быть другим) — генератор поставит номер по умолчанию.
            downloadKp(format, { ...pending, entityId, recipient })
          }
        />
      )}
    </div>
  )
}
